import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from '../../redux/hooks';
import BackButton from '../../components/back-button/BackButton';
import Loader from '../../components/loader/Loader';
import UploadImageModal from '../../components/upload-image-modal/UploadImageModal';
import EditSVG from '../../components/svgs/Edit.svg';
import { BUTTON_CONSTANTS, LABEL_CONSTANTS, MARKETPLACE_PROFILE_CONSTANTS, PAGE_HEADER_CONSTANTS } from '../../constants/common';
import { ROUTE_PATHS } from '../../constants/routePaths';
import { SOCIAL_FIELD_LABELS, SOCIAL_ICON_CONFIG } from '../../constants/data';
import { toastMessages } from '../../constants/errorMessages';
import {
  MarketPlaceProfile as MarketPlaceProfileType,
  MarketPlaceProfileSocialMedia,
} from '../../types/interfaces/marketPlace.interfaces';
import {
  createMarketplaceProfile,
  getMarketplaceProfile,
  updateMarketplaceProfile,
  getMarketplaceProfileImage,
  uploadMarketplaceProfileImage,
} from '../../api/marketPlaceProfileApi';
import { getUnitsSold } from '../../api/marketPlacePaymentTransactionOrderDetailApi';
import { showToast } from '../../services/toast.service';
import { formatBillingAddress } from '../../utils/addressHelpers';
import { pickInventoryListState } from '../inventory/Inventory';
import './breedrProfile.scss';

const defaultSocial: MarketPlaceProfileSocialMedia = {
  websiteUrl: '',
  facebookUrl: '',
  instagramUrl: '',
  youTubeUrl: '',
};

const BreederProfile: FC = () => {
  const { accountId: paramAccountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const activeAccountId = useAppSelector(state => state.activeAccount.accountId);
  const activeAccountName = useAppSelector(state => state.activeAccount.name);

  const accountId = paramAccountId !== undefined ? Number(paramAccountId) : activeAccountId;
  const isOwnProfile = activeAccountId != null && accountId === activeAccountId;
  const marketplaceBackPath = activeAccountId != null ? ROUTE_PATHS.APP_MARKETPLACE : ROUTE_PATHS.PUBLIC_MARKETPLACE;

  const [profile, setProfile] = useState<MarketPlaceProfileType | null>(null);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [isUploadImageModalOpen, setIsUploadImageModalOpen] = useState<boolean>(false);
  const [accountEmail, setAccountEmail] = useState<string>('');
  const [accountPhone, setAccountPhone] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(paramAccountId == null);
  const [form, setForm] = useState({
    displayName: activeAccountName ?? '',
    tagline: '',
    description: '',
    socialMedia: { ...defaultSocial },
  });

  const farmNameInputRef = useRef<HTMLInputElement>(null);

  const marketplaceOnboardingFlow = Boolean(
    (routerLocation.state as { marketplaceOnboardingFlow?: boolean } | null)?.marketplaceOnboardingFlow,
  );
  const marketplaceLocationState = routerLocation.state as {
    marketplaceRequiresAchSetup?: boolean;
    marketplaceRequiresDocumentUpload?: boolean;
    marketplacePayoutPending?: boolean;
  } | null;
  const marketplaceRequiresAchSetup = Boolean(marketplaceLocationState?.marketplaceRequiresAchSetup);
  const marketplaceRequiresDocumentUpload = Boolean(marketplaceLocationState?.marketplaceRequiresDocumentUpload);
  const marketplacePayoutPending = Boolean(marketplaceLocationState?.marketplacePayoutPending);
  const marketplaceNextPath = (routerLocation.state as { marketplaceNextPath?: string } | null)?.marketplaceNextPath;
  const marketplaceBackPathFromState = (routerLocation.state as { marketplaceBackPath?: string } | null)?.marketplaceBackPath;
  const listNavigationState = pickInventoryListState(routerLocation.state);
  const resolvedBackPath = marketplaceBackPathFromState ?? marketplaceBackPath;

  const navigateBack = useCallback(() => {
    navigate(resolvedBackPath, { state: listNavigationState });
  }, [listNavigationState, navigate, resolvedBackPath]);
  const [completedSales, setCompletedSales] = useState<number>(0);

  const fetchProfileImage = useCallback(async (id: number) => {
    try {
      const { data } = await getMarketplaceProfileImage(id);
      const url = URL.createObjectURL(data);
      setProfileImageUrl(previousUrl => {
        if (previousUrl) URL.revokeObjectURL(previousUrl);
        return url;
      });
    } catch {
      setProfileImageUrl(null);
    }
  }, []);

  const loadUnitsSold = useCallback(async (id: number) => {
    try {
      const { data } = await getUnitsSold(id);
      setCompletedSales(data ?? 0);
    } catch {
      setCompletedSales(0);
    }
  }, []);

  const buildFormFromProfile = (data: MarketPlaceProfileType) => ({
    displayName: data.displayName ?? '',
    tagline: data.tagline ?? '',
    description: data.description ?? '',
    socialMedia: {
      websiteUrl: data.socialMedia?.websiteUrl ?? '',
      facebookUrl: data.socialMedia?.facebookUrl ?? '',
      instagramUrl: data.socialMedia?.instagramUrl ?? '',
      youTubeUrl: data.socialMedia?.youTubeUrl ?? '',
    },
  });

  const setAccountDetailsFromProfile = (data: MarketPlaceProfileType) => {
    setAccountEmail(data.account?.contactEmail ?? '');
    setAccountPhone(data.account?.contactPhone ?? '');
    setLocation(formatBillingAddress(data.account?.addresses));
  };

  const fetchProfile = useCallback(async () => {
    if (accountId == null || Number.isNaN(accountId)) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const profileData = await getMarketplaceProfile(accountId).catch(() => null);

      if (profileData) {
        setProfile(profileData.data);
        setForm(buildFormFromProfile(profileData.data));
        setAccountDetailsFromProfile(profileData.data);
        setIsEditing(false);
        await Promise.all([fetchProfileImage(accountId), loadUnitsSold(accountId)]);
      } else {
        setProfile(null);
        setAccountEmail('');
        setAccountPhone('');
        setLocation('');
        setForm(prev => ({
          ...prev,
          displayName: activeAccountName ?? '',
        }));
        if (isOwnProfile) {
          setIsEditing(true);
        }
        await loadUnitsSold(accountId);
      }
    } finally {
      setLoading(false);
    }
  }, [accountId, fetchProfileImage, loadUnitsSold, activeAccountName, isOwnProfile]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleSave = async () => {
    if (accountId == null || !isOwnProfile || isSaving) return;
    try {
      setIsSaving(true);
      const basePayload = {
        displayName: form.displayName,
        tagline: form.tagline || undefined,
        description: form.description || undefined,
      };

      let savedProfile: MarketPlaceProfileType | null = null;
      if (profile) {
        const socialMediaId = profile.socialMediaId ?? profile.socialMedia?.socialMediaId;
        const { data } = await updateMarketplaceProfile(profile.marketPlaceProfileId, {
          marketPlaceProfileId: profile.marketPlaceProfileId,
          accountId,
          ...basePayload,
          socialMediaId,
          socialMedia: {
            socialMediaId,
            websiteUrl: form.socialMedia.websiteUrl || undefined,
            facebookUrl: form.socialMedia.facebookUrl || undefined,
            instagramUrl: form.socialMedia.instagramUrl || undefined,
            youTubeUrl: form.socialMedia.youTubeUrl || undefined,
          },
        });
        savedProfile = data;
      } else {
        const { data } = await createMarketplaceProfile({
          accountId,
          ...basePayload,
          websiteUrl: form.socialMedia.websiteUrl || undefined,
          facebookUrl: form.socialMedia.facebookUrl || undefined,
          instagramUrl: form.socialMedia.instagramUrl || undefined,
          youTubeUrl: form.socialMedia.youTubeUrl || undefined,
        });
        savedProfile = data;
      }
      if (savedProfile) {
        setProfile(savedProfile);
        setForm(buildFormFromProfile(savedProfile));
      }
      showToast.success(toastMessages.PROFILE_UPDATED);
      await fetchProfile();
      setIsEditing(false);
      if (marketplaceOnboardingFlow && isOwnProfile) {
        navigate(marketplaceNextPath ?? ROUTE_PATHS.APP_INVENTORY, {
          state: {
            ...(marketplaceRequiresAchSetup ? { marketplaceRequiresAchSetup: true } : {}),
            ...(marketplaceRequiresDocumentUpload ? { marketplaceRequiresDocumentUpload: true } : {}),
            ...(marketplacePayoutPending ? { marketplacePayoutPending: true } : {}),
            ...listNavigationState,
          },
        });
      }
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = useCallback(() => {
    if (isSaving) return;

    if (profile) {
      setForm(buildFormFromProfile(profile));
      setIsEditing(false);
      return;
    }

    navigateBack();
  }, [isSaving, navigateBack, profile]);

  const showBack = paramAccountId != null;
  const displayName = profile?.account?.name ?? profile?.displayName ?? activeAccountName ?? '';
  const hasSocialLinks = Boolean(
    profile?.socialMedia?.websiteUrl ||
      profile?.socialMedia?.instagramUrl ||
      profile?.socialMedia?.facebookUrl ||
      profile?.socialMedia?.youTubeUrl,
  );

  if (loading) {
    return <Loader loaderSize="medium" pageLoader />;
  }

  if (!profile && accountId != null && paramAccountId != null && !isOwnProfile) {
    return (
      <div className="marketplace-profile marketplace-profile--empty-state">
        {showBack && <BackButton onClick={navigateBack} />}
        <section className="marketplace-profile__section marketplace-profile__section--centered">
          <div className="marketplace-profile__empty">{MARKETPLACE_PROFILE_CONSTANTS.NO_BREEDER_DETAILS}</div>
        </section>
      </div>
    );
  }

  return (
    <div className="marketplace-profile">
      {showBack && <BackButton onClick={navigateBack} />}
      <section className="marketplace-profile__section marketplace-profile__header-card">
        <div className="marketplace-profile__header-card-top">
          <div>
            <h1 className="marketplace-profile__title">
              {isOwnProfile ? PAGE_HEADER_CONSTANTS.MY_PROFILE : LABEL_CONSTANTS.BREEDER_PROFILE}
            </h1>
            <p className="marketplace-profile__subtitle">
              {isOwnProfile
                ? MARKETPLACE_PROFILE_CONSTANTS.SUBTITLE_OWN_PROFILE
                : MARKETPLACE_PROFILE_CONSTANTS.SUBTITLE_OTHER_PROFILE}
            </p>
          </div>
        </div>

        <hr className="marketplace-profile__divider" />

        <div className="marketplace-profile__header-body">
          <div className="marketplace-profile__header-main">
            <div className="marketplace-profile__avatar-wrapper">
              {profileImageUrl ? (
                <img className="marketplace-profile__avatar-image" src={profileImageUrl} alt={displayName} />
              ) : (
                <div className="marketplace-profile__avatar" aria-hidden />
              )}
              {isOwnProfile && isEditing && profile && (
                <button
                  type="button"
                  className="marketplace-profile__avatar-edit-icon"
                  onClick={() => setIsUploadImageModalOpen(true)}
                  aria-label={MARKETPLACE_PROFILE_CONSTANTS.EDIT_PROFILE_IMAGE_ARIA}>
                  <EditSVG />
                </button>
              )}
            </div>
            <div>
              <h2 className="marketplace-profile__name">{displayName}</h2>
              {(accountEmail || accountPhone) && (
                <p className="marketplace-profile__contact">
                  {accountEmail && <span>{accountEmail}</span>}
                  {accountEmail && accountPhone && <br />}
                  {accountPhone && <span>{accountPhone}</span>}
                </p>
              )}
              {isOwnProfile && profile && !isEditing && (
                <button
                  type="button"
                  className="marketplace-profile__edit-link"
                  onClick={() => {
                    setIsEditing(true);
                    setTimeout(() => {
                      farmNameInputRef.current?.focus();
                    }, 100);
                  }}>
                  {LABEL_CONSTANTS.EDIT_PROFILE}
                </button>
              )}
            </div>
          </div>

          {isOwnProfile && (
            <div className="marketplace-profile__header-side">
              <div className="marketplace-profile__stat-box">
                <span className="marketplace-profile__stat-label">{MARKETPLACE_PROFILE_CONSTANTS.COMPLETED_SALES}</span>
                <span className="marketplace-profile__stat-value">
                  {completedSales}
                  <span className="marketplace-profile__stat-unit">{LABEL_CONSTANTS.UNITS}</span>
                </span>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="marketplace-profile__section">
        <h3 className="marketplace-profile__section-title">{LABEL_CONSTANTS.FARM_INFORMATION}</h3>
        <p className="marketplace-profile__section-desc">
          {isOwnProfile
            ? MARKETPLACE_PROFILE_CONSTANTS.FARM_SECTION_DESC_OWN
            : MARKETPLACE_PROFILE_CONSTANTS.FARM_SECTION_DESC_OTHER}
        </p>
        <hr className="marketplace-profile__divider" />

        {isEditing && isOwnProfile ? (
          <div className="marketplace-profile__form">
            <div className="form-row">
              <label>{LABEL_CONSTANTS.FARM_NAME} *</label>
              <div className="input-container">
                <input
                  ref={farmNameInputRef}
                  type="text"
                  value={form.displayName}
                  onChange={event => setForm(prevForm => ({ ...prevForm, displayName: event.target.value }))}
                  placeholder={MARKETPLACE_PROFILE_CONSTANTS.PLACEHOLDER_FARM_NAME}
                />
              </div>
            </div>
            <div className="form-row">
              <label>{MARKETPLACE_PROFILE_CONSTANTS.PRIMARY_SPECIALIZATION}</label>
              <div className="input-container">
                <input
                  type="text"
                  value={form.tagline}
                  onChange={event => setForm(prevForm => ({ ...prevForm, tagline: event.target.value }))}
                  placeholder={MARKETPLACE_PROFILE_CONSTANTS.PLACEHOLDER_TAGLINE}
                />
              </div>
            </div>
            <div className="form-row">
              <label>{MARKETPLACE_PROFILE_CONSTANTS.ABOUT_FARM_OWN}</label>
              <div className="input-container">
                <textarea
                  value={form.description}
                  onChange={event => setForm(prevForm => ({ ...prevForm, description: event.target.value }))}
                  placeholder={MARKETPLACE_PROFILE_CONSTANTS.PLACEHOLDER_ABOUT_FARM}
                  rows={5}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="marketplace-profile__readonly">
            <div className="marketplace-profile__field">
              <span className="marketplace-profile__label">{LABEL_CONSTANTS.FARM_NAME}</span>
              <span className={profile?.displayName ? undefined : 'marketplace-profile__empty-value'}>
                {profile?.displayName || '—'}
              </span>
            </div>
            <div className="marketplace-profile__field">
              <span className="marketplace-profile__label">{MARKETPLACE_PROFILE_CONSTANTS.LOCATION}</span>
              <span className={location ? undefined : 'marketplace-profile__empty-value'}>{location || '—'}</span>
            </div>
            <div className="marketplace-profile__field">
              <span className="marketplace-profile__label">{MARKETPLACE_PROFILE_CONSTANTS.PRIMARY_SPECIALIZATION}</span>
              <span className={profile?.tagline ? undefined : 'marketplace-profile__empty-value'}>
                {profile?.tagline || '—'}
              </span>
            </div>
            <div className="marketplace-profile__field">
              <span className="marketplace-profile__label">
                {isOwnProfile
                  ? MARKETPLACE_PROFILE_CONSTANTS.ABOUT_FARM_OWN
                  : MARKETPLACE_PROFILE_CONSTANTS.ABOUT_FARM_OTHER}
              </span>
              <span className={profile?.description ? undefined : 'marketplace-profile__empty-value'}>
                {profile?.description || '—'}
              </span>
            </div>
          </div>
        )}
      </section>

      {((isEditing && isOwnProfile) || hasSocialLinks) && (
        <section className="marketplace-profile__section">
          <h3 className="marketplace-profile__section-title">{LABEL_CONSTANTS.SOCIAL_LINKS}</h3>
          <p className="marketplace-profile__section-desc">
            {isOwnProfile
              ? MARKETPLACE_PROFILE_CONSTANTS.SOCIAL_SECTION_DESC_OWN
              : MARKETPLACE_PROFILE_CONSTANTS.SOCIAL_SECTION_DESC_OTHER}
          </p>
          <hr className="marketplace-profile__divider" />

          {isEditing && isOwnProfile ? (
            <div className="marketplace-profile__form">
              {(['websiteUrl', 'instagramUrl', 'facebookUrl', 'youTubeUrl'] as const).map(key => (
                <div className="form-row" key={key}>
                  <label>{SOCIAL_FIELD_LABELS[key]}</label>
                  <div className="input-container">
                    <input
                      type="url"
                      value={form.socialMedia[key] ?? ''}
                      onChange={event =>
                        setForm(prevForm => ({
                          ...prevForm,
                          socialMedia: { ...prevForm.socialMedia, [key]: event.target.value },
                        }))
                      }
                      placeholder={MARKETPLACE_PROFILE_CONSTANTS.PLACEHOLDER_URL}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="marketplace-profile__readonly marketplace-profile__social">
              <div className="marketplace-profile__social-icons">
                {SOCIAL_ICON_CONFIG.map(icon => {
                  const url = profile?.socialMedia?.[icon.key as keyof typeof profile.socialMedia] as string | undefined;
                  if (!url) return null;
                  return (
                    <a
                      key={icon.key}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="marketplace-profile__social-icon">
                      <img src={icon.src} alt={icon.alt} />
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      )}

      {isOwnProfile && isEditing && (
        <div className="marketplace-profile__actions">
          <button type="button" className="button secondary small red inverted" disabled={isSaving} onClick={handleCancel}>
            {BUTTON_CONSTANTS.CANCEL}
          </button>
          <button
            type="button"
            className={form.displayName.trim() && !isSaving ? 'button green small' : 'button green small disabled'}
            disabled={!form.displayName.trim() || isSaving}
            onClick={handleSave}>
            {isSaving ? 'Saving...' : BUTTON_CONSTANTS.SAVE}
          </button>
        </div>
      )}

      {isOwnProfile && accountId != null && profile && (
        <UploadImageModal
          isOpen={isUploadImageModalOpen}
          onClose={() => setIsUploadImageModalOpen(false)}
          postImage={async formData => {
            await uploadMarketplaceProfileImage(accountId, formData);
            await fetchProfileImage(accountId);
            showToast.success(toastMessages.USER_IMAGE_UPDATE_SUCCESS);
          }}
        />
      )}
    </div>
  );
};

export default BreederProfile;
